/**
 * Date utility functions to handle timezone issues
 */

/**
 * Check if a year is a leap year
 * @param {number} year - The year to check
 * @returns {boolean} True if leap year
 */
export const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)
}

/**
 * Converts a date string from the database to a proper local date
 * This prevents timezone offset issues when displaying dates
 * @param {string} dateString - Date string in YYYY-MM-DD format from database
 * @returns {Date|null} - Local date object or null if invalid
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null
  
  // Parse the date string as a local date to avoid timezone conversion
  const parts = dateString.split('-')
  if (parts.length !== 3) return null
  
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
  const day = parseInt(parts[2], 10)
  
  return new Date(year, month, day)
}

/**
 * Converts a UTC database timestamp to a date in the user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database (e.g., "2025-01-08T12:00:00.000Z")
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {Date|null} - Date object in user's timezone or null if invalid
 */
export const parseUTCToUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp || !timezone) return null
  
  try {
    // Parse the UTC timestamp
    const utcDate = new Date(utcTimestamp)
    if (isNaN(utcDate.getTime())) return null
    
    // Convert to user's timezone using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    const parts = formatter.formatToParts(utcDate)
    const year = parseInt(parts.find(p => p.type === 'year').value, 10)
    const month = parseInt(parts.find(p => p.type === 'month').value, 10) - 1
    const day = parseInt(parts.find(p => p.type === 'day').value, 10)
    
    return new Date(year, month, day)
  } catch (error) {
    console.error('Error parsing UTC timestamp to user timezone:', error)
    return null
  }
}

/**
 * Gets "today" in the user's timezone
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {Date} - Today's date in user's timezone
 */
export const getTodayInUserTimezone = (timezone) => {
  if (!timezone) {
    // Fallback to browser timezone
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  }
  
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    const parts = formatter.formatToParts(now)
    const year = parseInt(parts.find(p => p.type === 'year').value, 10)
    const month = parseInt(parts.find(p => p.type === 'month').value, 10) - 1
    const day = parseInt(parts.find(p => p.type === 'day').value, 10)
    
    return new Date(year, month, day)
  } catch (error) {
    console.error('Error getting today in user timezone:', error)
    // Fallback to browser timezone
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  }
}

/**
 * Gets today's date string in user's timezone
 * IMPORTANT: Use this instead of new Date().toISOString().split('T')[0] to avoid UTC timezone bugs
 * @param {string} timezone - User's timezone (e.g., "America/New_York")
 * @returns {string} - Date string in YYYY-MM-DD format in user's timezone
 */
export const getTodayDateString = (timezone) => {
  const todayDate = getTodayInUserTimezone(timezone)
  const year = todayDate.getFullYear()
  const month = String(todayDate.getMonth() + 1).padStart(2, '0')
  const day = String(todayDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets yesterday's date string in the user's timezone
 * @param {string} timezone - User's timezone (e.g., "America/New_York")
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const getYesterdayDateString = (timezone) => {
  const today = getTodayInUserTimezone(timezone)
  today.setDate(today.getDate() - 1)
  return formatDateAsYYYYMMDD(today)
}

/**
 * Formats a Date object as YYYY-MM-DD string
 * @param {Date} date - Date object to format
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export const formatDateAsYYYYMMDD = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Calculates the number of business days between two dates
 * Excludes weekends (when excludeWeekends is true) and custom excluded dates
 * @param {string|Date} startDate - Start date (ISO string, YYYY-MM-DD, or Date object)
 * @param {string|Date} endDate - End date (ISO string, YYYY-MM-DD, or Date object)
 * @param {Set<string>} excludedDates - Set of ISO date strings (YYYY-MM-DD) to exclude
 * @param {boolean} excludeWeekends - Whether to exclude Saturday/Sunday
 * @returns {number} - Number of business days (minimum 0)
 */
export const calculateBusinessDays = (startDate, endDate, excludedDates = new Set(), excludeWeekends = false) => {
  // Handle null/undefined inputs
  if (!startDate || !endDate) return 0

  // Parse dates to Date objects
  const parseDate = (date) => {
    if (date instanceof Date) return date
    if (typeof date === 'string') {
      // Handle ISO timestamp (has 'T')
      if (date.includes('T')) {
        return new Date(date)
      }
      // Handle YYYY-MM-DD format
      return parseLocalDate(date)
    }
    return null
  }

  const start = parseDate(startDate)
  const end = parseDate(endDate)

  if (!start || !end) return 0

  // Normalize to start of day
  const normalizedStart = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const normalizedEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate())

  // Handle case where end is before start
  if (normalizedEnd < normalizedStart) return 0

  // Same day completion = 0 business days elapsed
  if (normalizedStart.getTime() === normalizedEnd.getTime()) return 0

  // Count business days between start (exclusive) and end (inclusive)
  // This gives us elapsed business days: Fri -> Mon = 1 business day
  let businessDays = 0
  const current = new Date(normalizedStart)
  current.setDate(current.getDate() + 1) // Start from day after start

  while (current <= normalizedEnd) {
    const dayOfWeek = current.getDay() // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
    const dateString = formatDateAsYYYYMMDD(current)
    const isCustomExcluded = excludedDates.has(dateString)

    // Count this day if it's a business day
    const shouldExclude = (excludeWeekends && isWeekend) || isCustomExcluded
    if (!shouldExclude) {
      businessDays++
    }

    current.setDate(current.getDate() + 1)
  }

  return businessDays
}

/**
 * Gets the end of week date from a week start date
 * @param {string} weekStartDate - Week start date in YYYY-MM-DD format (assumes Monday)
 * @param {boolean} excludeWeekends - If true, end of week is Friday (+4 days); if false, Sunday (+6 days)
 * @returns {string|null} - End of week date in YYYY-MM-DD format
 */
export const getWeekEndDate = (weekStartDate, excludeWeekends = false) => {
  if (!weekStartDate) return null
  const startDate = parseLocalDate(weekStartDate)
  if (!startDate) return null

  const endDate = new Date(startDate)
  // If excluding weekends, end on Friday (+4 days from Monday)
  // Otherwise end on Sunday (+6 days from Monday)
  endDate.setDate(startDate.getDate() + (excludeWeekends ? 4 : 6))

  return formatDateAsYYYYMMDD(endDate)
}

/**
 * Formats a date for display, handling timezone issues
 * @param {string} dateString - Date string from database
 * @returns {string|null} - Formatted date string or null
 */
export const formatDisplayDate = (dateString) => {
  if (!dateString) return null
  
  const date = parseLocalDate(dateString)
  if (!date) return null
  
  return date.toLocaleDateString()
}

/**
 * Formats a date string for HTML date input
 * @param {string} dateString - Date string from database
 * @returns {string} - Formatted date string for HTML input (YYYY-MM-DD)
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return ''
  
  // If it's already in the correct format, return as is
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString
  }
  
  const date = parseLocalDate(dateString)
  if (!date) return ''
  
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  return `${year}-${month}-${day}`
}

/**
 * Checks if a date is today
 * @param {string} dateString - Date string from database
 * @returns {boolean} - True if the date is today
 */
export const isToday = (dateString) => {
  if (!dateString) return false
  
  const date = parseLocalDate(dateString)
  if (!date) return false
  
  const today = new Date()
  return date.toDateString() === today.toDateString()
}

/**
 * Checks if a UTC timestamp is today in the user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {boolean} - True if the date is today in user's timezone
 */
export const isTodayInUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp) return false
  
  const taskDate = parseUTCToUserTimezone(utcTimestamp, timezone)
  if (!taskDate) return false
  
  const todayInUserTZ = getTodayInUserTimezone(timezone)
  
  return taskDate.toDateString() === todayInUserTZ.toDateString()
}

/**
 * Checks if a date is tomorrow
 * @param {string} dateString - Date string from database
 * @returns {boolean} - True if the date is tomorrow
 */
export const isTomorrow = (dateString) => {
  if (!dateString) return false
  
  const date = parseLocalDate(dateString)
  if (!date) return false
  
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return date.toDateString() === tomorrow.toDateString()
}

/**
 * Checks if a date is overdue (past today)
 * @param {string} dateString - Date string from database
 * @returns {boolean} - True if the date is overdue
 */
export const isOverdue = (dateString) => {
  if (!dateString) return false

  const date = parseLocalDate(dateString)
  if (!date) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to beginning of day

  return date < today
}

/**
 * Checks if a date falls within the current week (Monday to Sunday) in user's timezone
 * @param {string} dateString - Date string (YYYY-MM-DD) or UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {boolean} - True if the date is within this week (Monday-Sunday)
 */
export const isDueThisWeek = (dateString, timezone) => {
  if (!dateString) return false

  // Determine if it's a date string or UTC timestamp
  const isDueDateString = dateString && !dateString.includes('T')

  let taskDate
  if (isDueDateString) {
    taskDate = parseLocalDate(dateString)
  } else {
    taskDate = parseUTCToUserTimezone(dateString, timezone)
  }

  if (!taskDate) return false

  // Get today in user's timezone
  const todayInUserTZ = getTodayInUserTimezone(timezone)

  // Get the current day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDayOfWeek = todayInUserTZ.getDay()

  // Calculate days until Monday (start of week)
  // If today is Sunday (0), we need to go back 6 days to Monday
  // If today is Monday (1), we need to go back 0 days
  // If today is Tuesday (2), we need to go back 1 day, etc.
  const daysFromMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1

  // Calculate week start (Monday at 00:00:00)
  const weekStart = new Date(todayInUserTZ)
  weekStart.setDate(todayInUserTZ.getDate() - daysFromMonday)
  weekStart.setHours(0, 0, 0, 0)

  // Calculate week end (Sunday at 23:59:59)
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  weekEnd.setHours(23, 59, 59, 999)

  // Normalize task date to compare
  const normalizedTaskDate = new Date(taskDate)
  normalizedTaskDate.setHours(0, 0, 0, 0)

  return normalizedTaskDate >= weekStart && normalizedTaskDate <= weekEnd
}

/**
 * Checks if a UTC timestamp is overdue in the user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {boolean} - True if the date is overdue in user's timezone
 */
export const isOverdueInUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp) return false
  
  const taskDate = parseUTCToUserTimezone(utcTimestamp, timezone)
  if (!taskDate) return false
  
  const todayInUserTZ = getTodayInUserTimezone(timezone)
  
  return taskDate < todayInUserTZ
}

/**
 * Gets a formatted due date with context (Today, Tomorrow, etc.)
 * @param {string} dateString - Date string from database
 * @returns {object|null} - Object with text, isOverdue, and isToday properties
 */
export const formatDueDateWithContext = (dateString) => {
  if (!dateString) return null
  
  if (isToday(dateString)) {
    return { text: 'Today', isOverdue: false, isToday: true }
  } else if (isTomorrow(dateString)) {
    return { text: 'Tomorrow', isOverdue: false, isToday: false }
  } else if (isOverdue(dateString)) {
    return { text: formatDisplayDate(dateString), isOverdue: true, isToday: false }
  } else {
    return { text: formatDisplayDate(dateString), isOverdue: false, isToday: false }
  }
}

/**
 * Gets today's date in YYYY-MM-DD format for date input
 * @returns {string} - Today's date string
 */
export const getTodayForInput = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets tomorrow's date in YYYY-MM-DD format for date input
 * @returns {string} - Tomorrow's date string
 */
export const getTomorrowForInput = () => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const year = tomorrow.getFullYear()
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
  const day = String(tomorrow.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets next week's date (7 days from today) in YYYY-MM-DD format for date input
 * @returns {string} - Next week's date string
 */
export const getNextWeekForInput = () => {
  const nextWeek = new Date()
  nextWeek.setDate(nextWeek.getDate() + 7)
  const year = nextWeek.getFullYear()
  const month = String(nextWeek.getMonth() + 1).padStart(2, '0')
  const day = String(nextWeek.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets next month's date (30 days from today) in YYYY-MM-DD format for date input
 * @returns {string} - Next month's date string
 */
export const getNextMonthForInput = () => {
  const nextMonth = new Date()
  nextMonth.setDate(nextMonth.getDate() + 30)
  const year = nextMonth.getFullYear()
  const month = String(nextMonth.getMonth() + 1).padStart(2, '0')
  const day = String(nextMonth.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets end of week (next Friday) in YYYY-MM-DD format for date input
 * @returns {string} - Next Friday's date string
 */
export const getEndOfWeekForInput = () => {
  const today = new Date()
  const currentDay = today.getDay() // 0 = Sunday, 5 = Friday, 6 = Saturday

  // Calculate days until Friday (5)
  // If today is Friday, use today. Otherwise, find next Friday.
  let daysUntilFriday
  if (currentDay === 5) {
    daysUntilFriday = 0 // Today is Friday
  } else if (currentDay === 6) {
    daysUntilFriday = 6 // Saturday -> next Friday is 6 days away
  } else {
    daysUntilFriday = 5 - currentDay // Days until next Friday
  }

  const nextFriday = new Date()
  nextFriday.setDate(today.getDate() + daysUntilFriday)

  const year = nextFriday.getFullYear()
  const month = String(nextFriday.getMonth() + 1).padStart(2, '0')
  const day = String(nextFriday.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets date range boundaries in user's timezone for analytics queries
 * @param {number} daysBack - Number of days back from today
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {object} - Object with startDate and endDate in ISO format for the user's timezone
 */
export const getDateRangeInUserTimezone = (daysBack, timezone) => {
  if (!timezone) {
    // Fallback to browser timezone calculation
    const now = new Date()
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysBack, 0, 0, 0, 0)
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  }

  try {
    // Get current date in user's timezone
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    const parts = formatter.formatToParts(now)
    const currentYear = parseInt(parts.find(p => p.type === 'year').value, 10)
    const currentMonth = parseInt(parts.find(p => p.type === 'month').value, 10) - 1
    const currentDay = parseInt(parts.find(p => p.type === 'day').value, 10)
    
    // Create end date (end of today in user's timezone)
    const endDate = new Date()
    endDate.setTime(Date.UTC(currentYear, currentMonth, currentDay, 23, 59, 59, 999))
    // Adjust for timezone offset
    const endOffset = getTimezoneOffsetMinutes(timezone, endDate)
    endDate.setMinutes(endDate.getMinutes() + endOffset)
    
    // Create start date (start of day daysBack ago in user's timezone)
    const startDate = new Date()
    startDate.setTime(Date.UTC(currentYear, currentMonth, currentDay - daysBack, 0, 0, 0, 0))
    // Adjust for timezone offset
    const startOffset = getTimezoneOffsetMinutes(timezone, startDate)
    startDate.setMinutes(startDate.getMinutes() + startOffset)
    
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  } catch (error) {
    console.error('Error calculating date range in user timezone:', error)
    // Fallback to UTC calculation
    const now = new Date()
    const endDate = new Date(now.getTime())
    const startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000)
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    }
  }
}

/**
 * Converts a UTC timestamp to hour of day in user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {number} - Hour of day (0-23) in user's timezone
 */
export const getHourInUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp || !timezone) return new Date(utcTimestamp).getHours()
  
  try {
    const date = new Date(utcTimestamp)
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false
    })
    
    const hour = formatter.format(date)
    return parseInt(hour, 10)
  } catch (error) {
    console.error('Error getting hour in user timezone:', error)
    return new Date(utcTimestamp).getHours()
  }
}

/**
 * Converts a UTC timestamp to day of week in user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {number} - Day of week (0=Sunday, 6=Saturday) in user's timezone
 */
export const getDayOfWeekInUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp || !timezone) return new Date(utcTimestamp).getDay()
  
  try {
    const date = new Date(utcTimestamp)
    // Create a temporary date to extract day of week
    const tempDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }))
    return tempDate.getDay()
  } catch (error) {
    console.error('Error getting day of week in user timezone:', error)
    return new Date(utcTimestamp).getDay()
  }
}

/**
 * Formats a UTC timestamp for display in user's timezone
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @returns {string} - Formatted date string in user's timezone
 */
export const formatDateInUserTimezone = (utcTimestamp, timezone) => {
  if (!utcTimestamp) return null
  
  try {
    const date = new Date(utcTimestamp)
    if (!timezone) {
      return date.toLocaleDateString()
    }
    
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date in user timezone:', error)
    return new Date(utcTimestamp).toLocaleDateString()
  }
}

/**
 * Gets timezone offset in minutes for a specific date
 * @param {string} timezone - User's timezone (e.g., "America/Chicago")
 * @param {Date} date - Date to get offset for
 * @returns {number} - Offset in minutes from UTC
 */
const getTimezoneOffsetMinutes = (timezone, date) => {
  try {
    const utcDate = new Date(date.getTime())
    const localDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }))
    return (localDate.getTime() - utcDate.getTime()) / (1000 * 60)
  } catch {
    return 0
  }
}

/**
 * Gets a relative date description (Today, Tomorrow, In 3 days, etc.)
 * @param {string} dateString - Date string from database
 * @returns {string|null} - Relative date description or null
 */
export const getRelativeDate = (dateString) => {
  if (!dateString) return null
  
  const date = parseLocalDate(dateString)
  if (!date) return null
  
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset to beginning of day for accurate comparison
  
  const normalizedDate = new Date(date)
  normalizedDate.setHours(0, 0, 0, 0) // Reset to beginning of day for accurate comparison
  
  const diffTime = normalizedDate - today
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Tomorrow'
  } else if (diffDays === -1) {
    return 'Yesterday'
  } else if (diffDays > 1 && diffDays <= 7) {
    return `In ${diffDays} days`
  } else if (diffDays < -1 && diffDays >= -7) {
    return `${Math.abs(diffDays)} days ago`
  } else if (diffDays > 7) {
    const weeks = Math.floor(diffDays / 7)
    if (weeks === 1) {
      return 'Next week'
    } else if (weeks <= 4) {
      return `In ${weeks} weeks`
    } else {
      const months = Math.floor(diffDays / 30)
      if (months === 1) {
        return 'Next month'
      } else {
        return `In ${months} months`
      }
    }
  } else if (diffDays < -7) {
    const weeks = Math.floor(Math.abs(diffDays) / 7)
    if (weeks === 1) {
      return 'Last week'
    } else if (weeks <= 4) {
      return `${weeks} weeks ago`
    } else {
      const months = Math.floor(Math.abs(diffDays) / 30)
      if (months === 1) {
        return 'Last month'
      } else {
        return `${months} months ago`
      }
    }
  }
  
  return formatDisplayDate(dateString)
}

/**
 * Formats a date string to a readable format (e.g., "Jan 1, 2025")
 * @param {string} dateString - Date string or ISO timestamp
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Unknown'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Gets the week boundaries (Monday-Sunday) for a given date
 * @param {Date} date - Any date within the desired week
 * @returns {{ weekStart: Date, weekEnd: Date }} - Monday (start) and Sunday (end) of the week
 */
export const getWeekBoundaries = (date) => {
  const inputDate = new Date(date)
  const dayOfWeek = inputDate.getDay()
  // Convert JS day (0=Sun, 1=Mon, ..., 6=Sat) to Monday-based (0=Mon, 1=Tue, ..., 6=Sun)
  const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1

  const weekStart = new Date(inputDate)
  weekStart.setDate(inputDate.getDate() - daysSinceMonday)
  weekStart.setHours(0, 0, 0, 0)

  const weekEnd = new Date(inputDate)
  weekEnd.setDate(inputDate.getDate() + (6 - daysSinceMonday))
  weekEnd.setHours(23, 59, 59, 999)

  return { weekStart, weekEnd }
}

/**
 * Gets the week containing a date, returning start/end date strings and label
 * Uses Monday-Sunday week format
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {{ startDate: string, endDate: string, label: string }} - Week info
 */
export const getCurrentWeek = (dateString) => {
  // Parse YYYY-MM-DD as local date, not UTC
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  const { weekStart, weekEnd } = getWeekBoundaries(date)

  return {
    startDate: formatDateAsYYYYMMDD(weekStart),
    endDate: formatDateAsYYYYMMDD(weekEnd),
    label: formatWeekLabel(weekStart)
  }
}

/**
 * Formats a week label with ordinal suffixes (e.g., "October 14th to 20th")
 * @param {Date} startDate - Start date of the week (Monday)
 * @returns {string} - Formatted week label with ordinal suffixes
 */
export const formatWeekLabel = (startDate) => {
  const start = new Date(startDate)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)

  const month = start.toLocaleDateString('en-US', { month: 'long' })
  const startDay = start.getDate()
  const endDay = end.getDate()

  // Add ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  return `${month} ${startDay}${getOrdinalSuffix(startDay)} to ${endDay}${getOrdinalSuffix(endDay)}`
}

/**
 * Get the first Monday of a given year
 * @param {number} year - The year
 * @returns {Date} - The first Monday of the year
 */
export const getFirstMondayOfYear = (year) => {
  const yearStart = new Date(year, 0, 1)
  const dayOfWeek = yearStart.getDay()
  // Sunday = 0, Monday = 1, etc.
  // If Sunday (0), first Monday is day 1
  // If Monday (1), first Monday is day 0 (Jan 1)
  // If Tuesday-Saturday (2-6), first Monday is 8 - dayOfWeek days later
  const daysUntilMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek)
  return new Date(year, 0, 1 + daysUntilMonday)
}

/**
 * Snap a date to the nearest Monday (mutates the date object)
 * @param {Date} date - Date to snap (mutated in place)
 * @returns {Date} - The same date object, snapped to Monday
 */
export const snapToMonday = (date) => {
  const day = date.getDay()
  const diff = day === 0 ? -6 : 1 - day
  date.setDate(date.getDate() + diff)
  return date
}

/**
 * Formats a week date range for display (e.g., "Dec 16-22" or "Dec 30 - Jan 5")
 * Handles partial weeks at year boundaries (e.g., "Jan 1 - 4" when Jan 1 is Thursday)
 * @param {string} weekStartDate - Week start date in YYYY-MM-DD format
 * @param {boolean} includeYear - Whether to include the year at the end
 * @returns {string|null} - Formatted date range or null if invalid
 */
export const formatWeekDateRange = (weekStartDate, includeYear = false) => {
  const startDate = parseLocalDate(weekStartDate)
  if (!startDate) return null

  const year = startDate.getFullYear()
  const yearStart = new Date(year, 0, 1)
  const yearEnd = new Date(year, 11, 31)
  const firstMonday = getFirstMondayOfYear(year)

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  // Check if this is a partial first week (date is before first Monday of the year)
  // This handles both Jan 1 stored dates and any date before first Monday
  const isPartialFirstWeek = startDate < firstMonday

  if (isPartialFirstWeek) {
    // Partial first week: Jan 1 to day before first Monday
    const endDate = new Date(firstMonday)
    endDate.setDate(endDate.getDate() - 1)

    const startMonth = monthNames[startDate.getMonth()]
    const endMonth = monthNames[endDate.getMonth()]
    const yearSuffix = includeYear ? `, ${year}` : ''

    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startMonth} ${startDate.getDate()} - ${endDate.getDate()}${yearSuffix}`
    }
    return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}${yearSuffix}`
  }

  // Regular weeks: snap to Monday for consistent display
  snapToMonday(startDate)

  // Calculate end date (6 days after start, or year end if truncated)
  const endDate = new Date(startDate)
  endDate.setDate(startDate.getDate() + 6)
  if (endDate > yearEnd) endDate.setTime(yearEnd.getTime())

  const startMonth = monthNames[startDate.getMonth()]
  const endMonth = monthNames[endDate.getMonth()]
  const yearSuffix = includeYear ? `, ${startDate.getFullYear()}` : ''

  if (startDate.getMonth() === endDate.getMonth()) {
    return `${startMonth} ${startDate.getDate()} - ${endDate.getDate()}${yearSuffix}`
  }
  return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}${yearSuffix}`
}

/**
 * Gets the last day of a quarter
 * @param {number} year - The year
 * @param {number} quarter - The quarter (1-4)
 * @returns {Date} - The last day of the quarter
 */
export const getEndOfQuarter = (year, quarter) => {
  // Q1 ends March 31, Q2 ends June 30, Q3 ends September 30, Q4 ends December 31
  const endMonth = quarter * 3 // 3, 6, 9, 12
  return new Date(year, endMonth, 0) // Day 0 of next month = last day of current month
}

/**
 * Gets the last day of a week given its start date
 * Handles partial weeks at year boundaries:
 * - Partial first week (Jan 1 before first Monday): ends day before first Monday
 * - Partial last week: ends Dec 31
 * - Regular weeks: ends 6 days after start
 * @param {string} weekStartDate - Week start date in YYYY-MM-DD format
 * @returns {Date|null} - The last day of the week or null if invalid
 */
export const getEndOfWeek = (weekStartDate) => {
  const start = parseLocalDate(weekStartDate)
  if (!start) return null

  const year = start.getFullYear()
  const firstMonday = getFirstMondayOfYear(year)
  const yearEnd = new Date(year, 11, 31)

  // Check if this is the partial first week (before first Monday)
  if (start < firstMonday) {
    // Partial first week ends the day before first Monday
    const end = new Date(firstMonday)
    end.setDate(end.getDate() - 1)
    return end
  }

  // Regular week: 6 days after start, but capped at year end
  const end = new Date(start)
  end.setDate(end.getDate() + 6)

  // If week crosses year boundary, cap at Dec 31
  if (end > yearEnd) {
    return yearEnd
  }

  return end
}
